import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { useToast } from "@/client/components/common/toast"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import type { User } from "@/lib/supabase/types"
import styles from "./edit-profile-page.module.css"

const DIRECTION_OPTIONS = [
  "前端开发",
  "后端开发",
  "全栈开发",
  "AI/ML",
  "移动端开发",
  "DevOps",
  "产品设计",
] as const

const NICKNAME_MAX = 20
const BIO_MAX = 100

interface FormErrors {
  nickname?: string
  bio?: string
  github_url?: string
  linkedin_url?: string
}

function validateUrl(value: string): boolean {
  if (!value) return true
  return value.startsWith("https://")
}

export default function EditProfilePage() {
  const { user } = useAuthStore()
  const toast = useToast()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // 表单字段
  const [nickname, setNickname] = useState("")
  const [bio, setBio] = useState("")
  const [school, setSchool] = useState("")
  const [company, setCompany] = useState("")
  const [direction, setDirection] = useState("")
  const [githubUrl, setGithubUrl] = useState("")
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)

  const [errors, setErrors] = useState<FormErrors>({})

  const fetchProfile = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase || !user) return

    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", user.id)
      .single()

    if (error) {
      toast.error("加载资料失败")
      setLoading(false)
      return
    }

    if (data) {
      const profile = data as User
      setNickname(profile.nickname ?? "")
      setBio(profile.bio ?? "")
      setSchool(profile.school ?? "")
      setCompany(profile.company ?? "")
      setDirection(profile.direction ?? "")
      setGithubUrl(profile.github_url ?? "")
      setLinkedinUrl(profile.linkedin_url ?? "")
      setAvatarUrl(profile.avatar_url)
    }

    setLoading(false)
  }, [user, toast])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  function validate(): boolean {
    const next: FormErrors = {}

    if (!nickname.trim()) {
      next.nickname = "昵称不能为空"
    } else if (nickname.length > NICKNAME_MAX) {
      next.nickname = `昵称最多 ${NICKNAME_MAX} 个字符`
    }

    if (bio.length > BIO_MAX) {
      next.bio = `简介最多 ${BIO_MAX} 个字符`
    }

    if (githubUrl && !validateUrl(githubUrl)) {
      next.github_url = "URL 必须以 https:// 开头"
    }

    if (linkedinUrl && !validateUrl(linkedinUrl)) {
      next.linkedin_url = "URL 必须以 https:// 开头"
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSave() {
    if (!validate()) return

    const supabase = getBrowserSupabaseClient()
    if (!supabase || !user) {
      toast.error("请先登录")
      return
    }

    setSaving(true)

    const { error } = await supabase
      .from("users")
      .update({
        nickname: nickname.trim(),
        bio: bio.trim() || null,
        school: school.trim() || null,
        company: company.trim() || null,
        direction: direction || null,
        github_url: githubUrl.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
      })
      .eq("id", user.id)

    setSaving(false)

    if (error) {
      toast.error("保存失败，请重试")
      return
    }

    toast.success("资料已更新")
    navigate("/me")
  }

  function updateError(field: keyof FormErrors, value: string) {
    setErrors((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  // 昵称首字母作为头像
  const avatarLetter = nickname.trim()
    ? nickname.trim()[0].toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? "U"

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className={styles.headerTitle}>编辑资料</span>
        </div>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          加载中...
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* 顶部导航 */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className={styles.headerTitle}>编辑资料</span>
      </div>

      {/* 头像 */}
      <div className={styles.avatarSection}>
        <div className={styles.avatar}>
          {avatarUrl ? (
            <img src={avatarUrl} alt={nickname} />
          ) : (
            avatarLetter
          )}
        </div>
        <span className={styles.avatarHint}>点击更换头像（即将开放）</span>
      </div>

      {/* 表单 */}
      <div className={styles.form}>
        {/* 昵称 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>昵称</label>
          <input
            className={styles.input}
            type="text"
            value={nickname}
            onChange={(e) => {
              setNickname(e.target.value)
              updateError("nickname", e.target.value)
            }}
            placeholder="输入昵称"
            maxLength={NICKNAME_MAX}
          />
          <div className={styles.inputRow}>
            {errors.nickname && (
              <span className={styles.errorText}>{errors.nickname}</span>
            )}
            <span className={`${styles.charCount} ${nickname.length > NICKNAME_MAX ? styles.warn : ""}`}>
              {nickname.length}/{NICKNAME_MAX}
            </span>
          </div>
        </div>

        {/* 个人简介 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>个人简介</label>
          <textarea
            className={styles.textarea}
            value={bio}
            onChange={(e) => {
              setBio(e.target.value)
              updateError("bio", e.target.value)
            }}
            placeholder="一句话介绍自己..."
            maxLength={BIO_MAX}
          />
          <div className={styles.inputRow}>
            {errors.bio && (
              <span className={styles.errorText}>{errors.bio}</span>
            )}
            <span className={`${styles.charCount} ${bio.length > BIO_MAX ? styles.warn : ""}`}>
              {bio.length}/{BIO_MAX}
            </span>
          </div>
        </div>

        {/* 学校 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>学校</label>
          <input
            className={styles.input}
            type="text"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="输入学校名称"
          />
        </div>

        {/* 公司 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>公司</label>
          <input
            className={styles.input}
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="输入公司名称"
          />
        </div>

        {/* 技术方向 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>技术方向</label>
          <select
            className={styles.select}
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <option value="">选择技术方向</option>
            {DIRECTION_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {/* GitHub */}
        <div className={styles.formGroup}>
          <label className={styles.label}>GitHub</label>
          <input
            className={styles.input}
            type="url"
            value={githubUrl}
            onChange={(e) => {
              setGithubUrl(e.target.value)
              updateError("github_url", e.target.value)
            }}
            placeholder="https://github.com/yourname"
          />
          {errors.github_url && (
            <span className={styles.errorText}>{errors.github_url}</span>
          )}
        </div>

        {/* LinkedIn */}
        <div className={styles.formGroup}>
          <label className={styles.label}>LinkedIn</label>
          <input
            className={styles.input}
            type="url"
            value={linkedinUrl}
            onChange={(e) => {
              setLinkedinUrl(e.target.value)
              updateError("linkedin_url", e.target.value)
            }}
            placeholder="https://linkedin.com/in/yourname"
          />
          {errors.linkedin_url && (
            <span className={styles.errorText}>{errors.linkedin_url}</span>
          )}
        </div>
      </div>

      {/* 底部保存按钮 */}
      <div className={styles.bottomAction}>
        <button
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "保存中..." : "保存修改"}
        </button>
      </div>
    </div>
  )
}
